<template>
    <div class="form-container card shadow-sm p-3">
      <!-- Cabeçalho -->
      <div class="header d-flex justify-content-between align-items-center">
        <h4 class="form-title">
          {{ registro.id ? "Editar Registro Financeiro" : "Novo Registro Financeiro" }}
        </h4>
        <button type="button" class="btn btn-secondary" @click="voltar">Voltar</button>
    </div>
  
      <!-- Espaçador -->
      <div class="spacer"></div>
  
      <!-- Formulário -->
      <BaseForm 
        :isFormValid="isFormValid"
        :isSubmitting="isSubmitting"
        @submit="salvarRegistro"
        @cancel="voltar"
      >
        <div class="form-section">
          <h5 class="section-title">Dados do Registro Financeiro</h5>
  
          <!-- Campo de Conta Financeira (Select) -->
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="conta-financeira" class="form-label">
                  Conta Financeira <span class="required">*</span>
                </label>
                <select
                  id="conta-financeira"
                  v-model="registro.conta_financeira_id"
                  class="form-control"
                  @change="atualizarContaSelecionada"
                >
                  <option value="" disabled>Selecione uma conta</option>
                  <option v-for="conta in contas" :key="conta.id" :value="conta.id">
                    {{ conta.apelido }} - {{ conta.agencia }} - {{ conta.numero_conta }}
                  </option>
                </select>
                <span v-if="formSubmitted && errors.conta_financeira_id" class="error-message">
                  {{ errors.conta_financeira_id }}
                </span>
              </div>
            </div>
  
            <!-- Data -->
            <div class="col-md-2">
              <div class="form-group">
                <label for="data">Data do saldo <span class="required">*</span></label>
                <input
                  id="data"
                  type="date"
                  v-model="registro.data"
                  class="form-control"
                  :class="{ 'error-input': formSubmitted && errors.data }"
                />
                <span v-if="formSubmitted && errors.data" class="error-message">
                  {{ errors.data }}
                </span>
              </div>
            </div>
            <!-- Saldo -->
            <div class="col-md-3">
              <div class="form-group">
                <label for="saldo">Saldo consolidado <span class="required">*</span></label>
                <input
                  id="saldo"
                  type="text"
                  v-model="saldoInput"
                  @blur="formatSaldo"
                  class="form-control"
                  :class="{ 'error-input': formSubmitted && errors.saldo }"
                  placeholder="Digite o saldo"
                />
                <span v-if="formSubmitted && errors.saldo" class="error-message">
                  {{ errors.saldo }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </BaseForm>
    </div>
  </template>
  
  <script setup>
  import { reactive, ref, computed, onMounted } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import axios from '@/services/axios';
  import BaseForm from '@/components/Layout/BaseForm.vue';
  import globalUtils from '@/utils/globalUtils';

  
  const router = useRouter();
  const route = useRoute();
  
  // Objeto que armazena os dados do registro financeiro
  const registro = reactive({
    id: null,
    conta_financeira_id: '',
    conta_financeira_nome: '',
    data: '',
    saldo: 0
  });
  
  // Array com as contas financeiras para preencher o select
  const contas = ref([]);
  
  const errors = reactive({});
  const formSubmitted = ref(false);
  const isSubmitting = ref(false);
  
  // Ref para o input de saldo (texto formatado)
  const saldoInput = ref('');
  
  // Validação: os campos obrigatórios devem ser preenchidos
  const isFormValid = computed(() => {
    return !!(registro.conta_financeira_id && registro.data && registro.saldo !== '');
  });
  
  // Função para navegar de volta à lista de registros
  function voltar() {
    router.push('/financeiro/registros-financeiros');
  }
  
  // Carregar registro financeiro para edição
  async function carregarRegistro(id) {
    try {
      const response = await axios.get(`/financeiro/registros-financeiros/${id}`);
      Object.assign(registro, response.data);
      // Se a data estiver em formato ISO, converte para "yyyy-MM-dd" usando sua função global (ou manualmente)
      // Supondo que o valor já esteja no formato correto, ou utilize sua função, se necessário.
      // Exemplo:
      registro.data = globalUtils.formatarDataParaInput(response.data.data);
  
      // Atualiza o saldoInput com o valor formatado
      saldoInput.value = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(registro.saldo);
    } catch (error) {
      console.error("Erro ao carregar dados do registro:", error);
    }
  }
  
  // Carregar contas financeiras para o select
  async function carregarContas() {
    try {
      const response = await axios.get(`/financeiro/contas-financeiras`);
      contas.value = response.data.data ? response.data.data : response.data;
    } catch (error) {
      console.error("Erro ao carregar contas financeiras:", error);
    }
  }
  
  // Atualiza o campo de exibição da conta financeira quando o select muda
  function atualizarContaSelecionada() {
    const contaSelecionada = contas.value.find(c => c.id === registro.conta_financeira_id);
    if (contaSelecionada) {
      registro.conta_financeira_nome = `${contaSelecionada.apelido} - ${contaSelecionada.agencia} - ${contaSelecionada.numero_conta}`;
    } else {
      registro.conta_financeira_nome = '';
    }
  }
  
  // Função para salvar o registro financeiro (criação ou edição)
  async function salvarRegistro() {
    // Força a formatação do valor do saldo antes de prosseguir
    formatSaldo();

    formSubmitted.value = true;
    if (isSubmitting.value) return;
    isSubmitting.value = true;

    if (!isFormValid.value) {
        isSubmitting.value = false;
        console.error("O formulário contém erros: Campos obrigatórios não preenchidos.");
        return;
    }

    try {
        const url = registro.id
        ? `/financeiro/registros-financeiros/${registro.id}`
        : `/financeiro/registros-financeiros`;
        const method = registro.id ? 'put' : 'post';

        await axios[method](url, registro);
        router.push('/financeiro/registros-financeiros');
    } catch (error) {
        errors.backend = error.response?.data?.error || "Erro ao salvar o registro financeiro.";
        console.error("Erro ao salvar registro:", error);
    } finally {
        isSubmitting.value = false;
    }
    }
  
  // Função para formatar o valor digitado no input de saldo
  function formatSaldo() {
    // Remove pontos e substitui vírgula por ponto para converter em número
    const numericValue = parseFloat(saldoInput.value.replace(/\./g, '').replace(',', '.'));
    registro.saldo = isNaN(numericValue) ? 0 : numericValue;
    // Atualiza o input com o valor formatado
    saldoInput.value = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(registro.saldo);
  }
  
  // Se estiver editando, carrega o registro; também carrega as contas disponíveis
  onMounted(() => {
    const registroId = route.params.id;
    if (registroId) {
      carregarRegistro(registroId);
    }
    carregarContas();
  });
  </script>
  
  <style scoped>
  .form-container {
    margin: 20px;
  }
  .form-section {
    background: white;
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    margin-bottom: 1rem;
  }
  .section-title {
    font-size: 0.9rem;
    font-weight: bold;
    margin-bottom: 10px;
  }
  .disabled-input {
    background: #e9ecef;
    border: 1px solid #ced4da;
  }
  .required {
    color: red;
  }
  .spacer {
    margin-bottom: 20px;
  }
  </style>
  